import type { DiagramNode, DiagramView } from '../types';
import { CANVAS, toneColor } from '../theme';

import { Caption, FONT, frameProps, valueWidth, labelWidth } from './shared';

const PAD = 32;
const MIN_NODE_W = 140;
const NODE_H = 60;
const ATTR_LINE_H = 16;
const LABEL_LINE_H = 18;
/** Horizontal breathing room kept between a node's longest text line and its border. */
const TEXT_PAD_X = 20;

/** A node's own label may be multi-line (authors separate lines with `\n`) — split it once and reuse everywhere sizing/drawing needs line count. */
function labelLines(node: DiagramNode): string[] {
  return node.label.split('\n');
}

/** Width wide enough for the node's longest label/attribute line, never narrower than the old fixed box so short labels keep their familiar size. */
function nodeWidth(node: DiagramNode): number {
  const lines = labelLines(node);
  const attrs = Object.entries(node.attributes ?? {});
  const longestLabel = Math.max(0, ...lines.map((line) => valueWidth(line)));
  const longestAttr = Math.max(0, ...attrs.map(([key, value]) => labelWidth(`${key}: ${value}`)));
  return Math.max(MIN_NODE_W, longestLabel + TEXT_PAD_X * 2, longestAttr + TEXT_PAD_X * 2);
}

function nodeHeight(node: DiagramNode): number {
  const extraLabelLines = labelLines(node).length - 1;
  const attrCount = Object.keys(node.attributes ?? {}).length;
  return NODE_H + extraLabelLines * LABEL_LINE_H + attrCount * ATTR_LINE_H;
}

/** Point on `node`'s border closest to `toward` — an edge should stop at the shape's edge, not its center, or the arrowhead would land inside the box. */
function borderPoint(node: DiagramNode, width: number, height: number, toward: { x: number; y: number }) {
  const dx = toward.x - node.x;
  const dy = toward.y - node.y;
  const len = Math.hypot(dx, dy) || 1;
  const halfW = width / 2;
  const halfH = height / 2;
  // `t` is the fraction of the (dx, dy) vector needed to reach the rectangle's
  // edge; the actual pixel distance from the center is `t * len`, not `t` itself.
  const t = 1 / Math.max(Math.abs(dx) / halfW, Math.abs(dy) / halfH, 1e-6);
  const dist = Math.min(len, t * len);
  return { x: node.x + (dx / len) * dist, y: node.y + (dy / len) * dist };
}

function NodeShape({ node, width, height }: { node: DiagramNode; width: number; height: number }) {
  const color = toneColor(node.tone, 'muted');
  const attrs = Object.entries(node.attributes ?? {});
  const lines = labelLines(node);
  const shape = node.shape ?? 'box';
  const left = node.x - width / 2;
  const top = node.y - height / 2;
  const labelBlockH = (lines.length - 1) * LABEL_LINE_H;
  const labelTop = top + (attrs.length > 0 ? 20 : (height - labelBlockH) / 2);

  return (
    <g>
      {shape === 'circle' ? (
        <ellipse cx={node.x} cy={node.y} rx={width / 2} ry={height / 2} fill={CANVAS.cell} stroke={color} strokeWidth={1.5} />
      ) : shape === 'diamond' ? (
        <polygon
          points={`${node.x},${top} ${node.x + width / 2},${node.y} ${node.x},${top + height} ${node.x - width / 2},${node.y}`}
          fill={CANVAS.cell}
          stroke={color}
          strokeWidth={1.5}
        />
      ) : (
        <rect x={left} y={top} width={width} height={height} rx={10} fill={CANVAS.cell} stroke={color} strokeWidth={1.5} />
      )}
      <text
        x={node.x}
        y={labelTop}
        fill={CANVAS.text}
        fontSize={FONT.value}
        fontWeight={700}
        textAnchor="middle"
        dominantBaseline="central"
      >
        {lines.map((line, i) => (
          <tspan key={i} x={node.x} dy={i === 0 ? 0 : LABEL_LINE_H}>
            {line}
          </tspan>
        ))}
      </text>
      {attrs.map(([key, value], i) => (
        <text
          key={key}
          x={node.x}
          y={top + 20 + labelBlockH + FONT.value / 2 + 8 + i * ATTR_LINE_H}
          fill={CANVAS.textDim}
          fontSize={FONT.label}
          textAnchor="middle"
          dominantBaseline="central"
        >
          {key}: {value}
        </text>
      ))}
    </g>
  );
}

export function DiagramRenderer({ view }: { view: DiagramView }) {
  const nodes = view.nodes ?? [];
  const edges = view.edges ?? [];
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const widths = new Map(nodes.map((n) => [n.id, nodeWidth(n)]));
  const heights = new Map(nodes.map((n) => [n.id, nodeHeight(n)]));

  const captionY = view.caption ? PAD / 2 : 0;
  const yShift = view.caption ? 24 : 0;

  const maxX = nodes.length ? Math.max(...nodes.map((n) => n.x + (widths.get(n.id) ?? MIN_NODE_W) / 2)) : MIN_NODE_W;
  const minX = nodes.length ? Math.min(...nodes.map((n) => n.x - (widths.get(n.id) ?? MIN_NODE_W) / 2)) : 0;
  const maxY = nodes.length ? Math.max(...nodes.map((n) => n.y + (heights.get(n.id) ?? NODE_H) / 2)) : NODE_H;
  const minY = nodes.length ? Math.min(...nodes.map((n) => n.y - (heights.get(n.id) ?? NODE_H) / 2)) : 0;

  const offsetX = PAD - minX;
  const offsetY = PAD + yShift - minY;
  const width = maxX - minX + PAD * 2;
  const height = maxY - minY + PAD * 2 + yShift;

  const shifted = (n: DiagramNode): DiagramNode => ({ ...n, x: n.x + offsetX, y: n.y + offsetY });

  return (
    <svg {...frameProps(width, height, view.caption)}>
      <title>{view.caption ?? 'Diagram'}</title>
      {view.caption ? <Caption text={view.caption} x={PAD} y={captionY} /> : null}
      {edges.map((edge) => {
        const source = byId.get(edge.source);
        const target = byId.get(edge.target);
        if (!source || !target) return null;
        const s = shifted(source);
        const t = shifted(target);
        const from = borderPoint(s, widths.get(source.id) ?? MIN_NODE_W, heights.get(source.id) ?? NODE_H, t);
        const to = borderPoint(t, widths.get(target.id) ?? MIN_NODE_W, heights.get(target.id) ?? NODE_H, s);
        const midX = (from.x + to.x) / 2;
        const midY = (from.y + to.y) / 2;
        return (
          <g key={edge.id}>
            <line
              x1={from.x}
              y1={from.y}
              x2={to.x}
              y2={to.y}
              stroke={CANVAS.textFaint}
              strokeWidth={1.5}
              markerEnd={edge.directed ? 'url(#diagram-arrow)' : undefined}
            />
            {edge.label ? (
              <text x={midX} y={midY - 6} fill={CANVAS.textDim} fontSize={FONT.label} textAnchor="middle">
                {edge.label}
              </text>
            ) : null}
          </g>
        );
      })}
      <defs>
        <marker id="diagram-arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 Z" fill={CANVAS.textFaint} />
        </marker>
      </defs>
      {nodes.map((node) => (
        <NodeShape
          key={node.id}
          node={shifted(node)}
          width={widths.get(node.id) ?? MIN_NODE_W}
          height={heights.get(node.id) ?? NODE_H}
        />
      ))}
    </svg>
  );
}

export default DiagramRenderer;
